import { ColumnFiltersState } from "@tanstack/react-table";

/** Column the Kilder table holds its source selection on (the leveled root). */
export const DATASOURCE_FILTER_ID = "datasource";

/**
 * The single source the table is scoped to, or `undefined` for "every source" —
 * which is also what a multi-value selection means, since the source-specific
 * columns and filters only make sense for one source at a time.
 */
export function getSelectedDatasource(
  columnFilters: ColumnFiltersState
): string | undefined {
  const entry = columnFilters.find(
    (filter) => filter.id === DATASOURCE_FILTER_ID
  );
  if (!entry) return undefined;

  if (Array.isArray(entry.value)) {
    return entry.value.length === 1 ? String(entry.value[0]) : undefined;
  }

  return typeof entry.value === "string" ? entry.value : undefined;
}

/**
 * Whether `columnFilters` is already scoped to `datasourceId`.
 *
 * Compared case-insensitively on purpose: a breadcrumb frame carries a
 * lowercased source id, while the filter configuration keeps its own casing
 * for its source keys. An exact comparison would read a return to such a list
 * as a move to a different source, and re-scoping it would overwrite the
 * configuration's key with the lowercased id, which then matches no source at
 * all.
 */
export function isScopedToDatasource(
  columnFilters: ColumnFiltersState,
  datasourceId: string
): boolean {
  const selected = getSelectedDatasource(columnFilters);

  return (
    selected !== undefined &&
    selected.toLowerCase() === datasourceId.toLowerCase()
  );
}

/**
 * `columnFilters` scoped to `datasourceId`, keeping every other filter the user
 * has set. Filters belonging to a source they are leaving are pruned by the
 * toolbar when it sees the root selection change, so nothing is dropped here.
 *
 * Returns the given state unchanged when it is already scoped to that source,
 * so a caller can tell a re-scope apart from a no-op.
 */
export function withDatasourceFilter(
  columnFilters: ColumnFiltersState,
  datasourceId: string
): ColumnFiltersState {
  if (isScopedToDatasource(columnFilters, datasourceId)) {
    return columnFilters;
  }

  const others = columnFilters.filter(
    (filter) => filter.id !== DATASOURCE_FILTER_ID
  );

  return [{ id: DATASOURCE_FILTER_ID, value: datasourceId }, ...others];
}
